import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  SidebarConfig,
  SidebarContextProps,
  SidebarProviderProps,
} from './types';

export const SidebarContext = createContext<SidebarContextProps>({
  items: [],
  isOpen: false,
});

const mergeSidebarConfig = (
  config: SidebarConfig,
  updatedConfig: SidebarConfig
) => {
  return {
    ...config,
    ...updatedConfig,
    items: config?.items.map((item) => {
      const updatedConfigItem = updatedConfig?.items.find(
        (updatedItem) => item.id === updatedItem.id
      );

      return {
        ...item,
        ...(updatedConfigItem || {}),
      };
    }),
  };
};

export const SidebarProvider = ({ config, children }: SidebarProviderProps) => {
  const [isOpen, setSidebarOpen] = useState(config?.isOpen);
  const [sidebarConfig, setSidebarConfig] = useState(config);

  useEffect(() => {
    setSidebarConfig((prevConfig) => mergeSidebarConfig(prevConfig, config));
  }, [config]);

  const value = useMemo(
    () => ({
      ...sidebarConfig,
      isOpen,
      setSidebarOpen,
      setSidebarConfig,
    }),
    [isOpen, setSidebarOpen, sidebarConfig, setSidebarConfig]
  ) as SidebarContextProps;

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebarContext = () => useContext(SidebarContext);

export const useToggleSidebarHandler = () => {
  const { setSidebarOpen } = useContext(SidebarContext);

  return useCallback(() => {
    if (setSidebarOpen) {
      setSidebarOpen((prev) => !prev);
    }
  }, [setSidebarOpen]);
};

const sanitizePath = (url = '') => {
  return url
    .replace(/#.+$/, '') // remove hash part
    .replace(/\?.+$/, '') // remove query string
    .replace(/^\/+/, '') // remove beginning slashes
    .replace(/\/+$/, '') // remove trailing slashes
    .trim();
};

const getLocation = () => (window?.location ?? {}) as Location;

export const isRootLinkActive = (path: string) => {
  const location = getLocation();
  const sanitizedLocationPath = sanitizePath(location.pathname);
  const sanitizedPath = sanitizePath(path);

  if (sanitizedPath === '') {
    return sanitizedLocationPath === sanitizedPath;
  }

  return sanitizedLocationPath.startsWith(sanitizedPath);
};

export const isPartOfCurrentPath = (path: string): boolean => {
  const location = getLocation();
  const sanitizedLocationPath = sanitizePath(location.pathname);
  const sanitizedPath = sanitizePath(path);

  return new Set(sanitizedLocationPath.split('/')).has(sanitizedPath);
};
